import { useCallback, useEffect, useState } from 'react'

import { withContextRequestHeader } from '@/auth/contextRequestHeader'
import { env } from '@/config/env'
import {
  isHttpStatusInfrastructureFailure,
  notifyServiceLikelyDown,
} from '@/services/serviceReachabilityMonitor'

export interface MercureSubscribeResponse {
  token: string
  topics: string[]
}

/**
 * Fetches the Mercure subscriber JWT and allowed topics from the backend.
 * The JWT is used to authenticate with the Mercure hub; topics must be used
 * as-is when subscribing (they match what the server publishes to).
 */
export async function getMercureSubscribeToken(
  authToken: string | null
): Promise<MercureSubscribeResponse | null> {
  if (!authToken) return null
  let res: Response
  try {
    res = await fetch(`${env.API_URL}/mercure-subscribe-token`, {
      headers: withContextRequestHeader({
        Authorization: `Bearer ${authToken}`,
      }),
    })
  } catch {
    notifyServiceLikelyDown()
    return null
  }
  if (!res.ok) {
    if (isHttpStatusInfrastructureFailure(res.status)) {
      notifyServiceLikelyDown()
    }
    return null
  }
  const json = await res.json()
  const token = typeof json?.token === 'string' ? json.token : null
  if (!token) return null
  const topics = Array.isArray(json?.topics)
    ? (json.topics as unknown[]).filter(
        (x): x is string => typeof x === 'string'
      )
    : []
  return { token, topics }
}

export interface UseMercureSubscribeTokenResult {
  token: string | null
  topics: string[]
  loading: boolean
  error: Error | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch and hold the Mercure subscribe JWT and allowed topics.
 * Refetches when authToken changes. Does not open an EventSource.
 */
export function useMercureSubscribeToken(
  authToken: string | null
): UseMercureSubscribeTokenResult {
  const [token, setToken] = useState<string | null>(null)
  const [topics, setTopics] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchToken = useCallback(async () => {
    if (!authToken) {
      setToken(null)
      setTopics([])
      setLoading(false)
      setError(null)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const data = await getMercureSubscribeToken(authToken)
      if (data) {
        setToken(data.token)
        setTopics(data.topics)
      } else {
        setToken(null)
        setTopics([])
      }
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)))
      setToken(null)
      setTopics([])
    } finally {
      setLoading(false)
    }
  }, [authToken])

  useEffect(() => {
    fetchToken()
  }, [fetchToken])

  return { token, topics, loading, error, refetch: fetchToken }
}
